import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Alert, Button, IconButton, Snackbar } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { guestReplayLimitReachedToast } from '../auth/guestAccessCopy';
import { GUEST_DATA_MERGED_TOAST } from '../auth/guestMergeToast';
import { BRAND_GOLD } from '../navigation/navTabStyles';
import {
  registerApiActionNotifier,
  registerApiNotifier,
  notifyApiUser,
  type ApiActionNotification,
} from './apiNotification';

const OFFLINE_MESSAGE =
  'You appear to be offline. Check your internet connection.';

const goldAlertSx = {
  bgcolor: BRAND_GOLD,
  color: 'grey.900',
  fontWeight: 600,
  '& .MuiAlert-icon': { color: 'grey.900' },
} as const;

const isGoldApiToast = (text: string): boolean =>
  text === GUEST_DATA_MERGED_TOAST ||
  text === guestReplayLimitReachedToast ||
  text.startsWith('Import queued') ||
  text.startsWith('Import finished') ||
  (text.startsWith('Import from ') && text.endsWith(' finished.')) ||
  (text.startsWith('Your games from ') && text.endsWith(' are ready.')) ||
  (text.startsWith('Imported ') && text.includes(' new game')) ||
  text.startsWith('Analysis queued');

const ApiNotificationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [message, setMessage] = useState('');
  const [open, setOpen] = useState(false);
  const [actionNotice, setActionNotice] = useState<ApiActionNotification | null>(
    null,
  );
  const [actionOpen, setActionOpen] = useState(false);

  const showMessage = useCallback((text: string) => {
    setMessage(text);
    setOpen(true);
  }, []);

  const showActionNotice = useCallback((payload: ApiActionNotification) => {
    setActionNotice(payload);
    setActionOpen(true);
  }, []);

  useLayoutEffect(() => {
    registerApiNotifier(showMessage);
    registerApiActionNotifier(showActionNotice);
    return () => {
      registerApiNotifier(null);
      registerApiActionNotifier(null);
    };
  }, [showMessage, showActionNotice]);

  useEffect(() => {
    const notifyOffline = () => {
      notifyApiUser(OFFLINE_MESSAGE);
    };

    const onOnline = () => {
      notifyApiUser('Connection restored.');
    };

    let wasOnline = navigator.onLine;

    const syncOnlineState = () => {
      const online = navigator.onLine;
      if (wasOnline && !online) {
        notifyOffline();
      }
      wasOnline = online;
    };

    window.addEventListener('offline', notifyOffline);
    window.addEventListener('online', onOnline);
    const pollId = window.setInterval(syncOnlineState, 1000);

    return () => {
      window.removeEventListener('offline', notifyOffline);
      window.removeEventListener('online', onOnline);
      window.clearInterval(pollId);
    };
  }, []);

  const isGoldToast = isGoldApiToast(message);

  return (
    <>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={7000}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setOpen(false)}
          severity={isGoldToast ? 'warning' : 'error'}
          variant="filled"
          sx={{
            width: '100%',
            maxWidth: 480,
            ...(isGoldToast ? goldAlertSx : {}),
          }}
        >
          {message}
        </Alert>
      </Snackbar>
      <Snackbar
        open={actionOpen}
        autoHideDuration={10000}
        onClose={() => setActionOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={actionNotice?.variant === 'gold' ? 'warning' : 'success'}
          variant="filled"
          sx={{
            width: '100%',
            maxWidth: 480,
            ...(actionNotice?.variant === 'gold' ? goldAlertSx : {}),
          }}
          action={
            actionNotice ? (
              <>
                <Button
                  color="inherit"
                  size="small"
                  component={RouterLink}
                  to={actionNotice.actionTo}
                  onClick={() => setActionOpen(false)}
                >
                  {actionNotice.actionLabel}
                </Button>
                <IconButton
                  aria-label="Dismiss"
                  color="inherit"
                  size="small"
                  onClick={() => setActionOpen(false)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            ) : undefined
          }
        >
          {actionNotice?.message ?? ''}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ApiNotificationProvider;
